import { useState, useEffect, useCallback } from 'react';
import aiTermsService, { AITermsStatus, TermsType } from '../services/aiTermsService';
import requestDeduplicator from '../services/requestDeduplicator';
import { getStoredAuthToken, isJwtExpired, shouldEndSessionForAuthError } from '../utils/sessionAuth';

const CACHE_DURATION = 10 * 60 * 1000; // 10 minutes (success)
const ERROR_BACKOFF_MS = 2 * 60 * 1000; // 2 minutes after error before retry (stops 429/403 loops)

interface UseAITermsOptions {
    termsType?: TermsType;
}

interface CheckStatusOptions {
    force?: boolean;
}

const getHttpStatusCode = (err: unknown): number | undefined => {
    if (!err || typeof err !== 'object') return undefined;
    const e = err as { response?: { status?: number }; status?: number };
    return e.response?.status ?? (typeof e.status === 'number' ? e.status : undefined);
};

export const useAITerms = (options: UseAITermsOptions = {}) => {
    const { termsType = 'aptive_index' } = options;
    
    const [status, setStatus] = useState<AITermsStatus | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastFetchTime, setLastFetchTime] = useState<number>(0);
    const [lastErrorTime, setLastErrorTime] = useState<number>(0);
    /** True when the API returned 401 – token invalid/expired; caller should treat as not logged in */
    const [isUnauthenticated, setIsUnauthenticated] = useState(false);
    /** True when API is unreachable or service is temporarily unavailable */
    const [isServiceInterrupted, setIsServiceInterrupted] = useState(false);

    const getAuthToken = () => getStoredAuthToken();

    const checkStatus = useCallback(async (options: CheckStatusOptions = {}) => {
        const { force = false } = options;
        try {
            const token = getAuthToken();
            if (!token) {
                setStatus(null);
                setError(null);
                setIsUnauthenticated(false);
                setIsLoading(false);
                return;
            }

            if (isJwtExpired(token)) {
                setStatus(null);
                setError(null);
                setIsUnauthenticated(true);
                setIsServiceInterrupted(false);
                setIsLoading(false);
                return;
            }

            // Check if we have a recent cached result or are in error backoff (stops infinite retry on 429/403)
            const now = Date.now();
            if (!force && status !== null && (now - lastFetchTime) < CACHE_DURATION) {
                setIsLoading(false);
                return;
            }
            if (!force && lastErrorTime > 0 && (now - lastErrorTime) < ERROR_BACKOFF_MS) {
                setIsLoading(false);
                return;
            }

            setIsLoading(true);
            setError(null);
            
            // Use deduplication to prevent multiple simultaneous calls
            const result = await requestDeduplicator.execute(
                `ai-terms-status-${termsType}`,
                () => aiTermsService.checkStatus(termsType)
            );
            
            setStatus(result);
            setLastFetchTime(now);
            setLastErrorTime(0); // Clear error backoff on success
            setIsUnauthenticated(false);
            setIsServiceInterrupted(false);
        } catch (err: any) {
            const now = Date.now();
            setLastFetchTime(now);
            setLastErrorTime(now); // Backoff before retry to prevent infinite loop
            const statusCode = getHttpStatusCode(err);
            // 401 = no token / unauthorized; 403 = token invalid or expired (server uses 403 for jwt.verify failure)
            if (shouldEndSessionForAuthError(err, `/admin/ai-terms/status`)) {
                setStatus(null);
                setError(null);
                setIsUnauthenticated(true); // Treat as not logged in – clear session and show login
                setIsServiceInterrupted(false);
            } else if (statusCode === 429) {
                // Do not assume terms are unaccepted — show retry overlay instead of the terms modal.
                setError('Too many requests. Please wait a moment and try again.');
                setIsServiceInterrupted(true);
            } else {
                const isServerUnavailable = statusCode === 500 || statusCode === 502 || statusCode === 503 || statusCode === 504;
                const isNetworkError = !statusCode;
                setIsServiceInterrupted(isServerUnavailable || isNetworkError);
                setError(err?.message || 'Failed to check AI terms status');
                setStatus({ 
                    aiTermsAccepted: false, 
                    aiTermsAcceptedAt: null,
                    currentVersion: '1.0.0',
                    userVersion: null,
                    needsUpdate: false,
                    termsType
                });
            }
        } finally {
            setIsLoading(false);
        }
    }, [status, lastFetchTime, lastErrorTime, termsType]);

    const acceptTerms = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            const result = await aiTermsService.acceptTerms(termsType);
            setStatus(result);
            return result;
        } catch (err: any) {
            setError(err.message || 'Failed to accept AI terms');
            throw err;
        } finally {
            setIsLoading(false);
        }
    }, [termsType]);

    useEffect(() => {
        checkStatus();
    }, [checkStatus]);

    // Determine if user needs to accept terms (either never accepted or needs update)
    const needsTermsAcceptance = !status?.aiTermsAccepted || status?.needsUpdate;

    return {
        status,
        isLoading,
        error,
        /** True when API returned 401 – treat as not logged in; do not show terms modal */
        isUnauthenticated,
        /** True when API is unreachable or service is temporarily unavailable */
        isServiceInterrupted,
        checkStatus,
        acceptTerms,
        hasAcceptedTerms: status?.aiTermsAccepted && !status?.needsUpdate,
        needsTermsAcceptance,
        currentVersion: status?.currentVersion || '1.0.0',
        userVersion: status?.userVersion,
        needsUpdate: status?.needsUpdate || false,
        termsType
    };
};
